
import { Pioche, Player, Players, Animate, Card } from "@/skyjo/game";
import { axios, socket } from '@/lib/axios'

enum GAMEROUNDS {
    start,
    chooseTwoCards,
    chooseStackOrDiscard,
    keepOrDiscard,
    exchangeDiscardCard,
    exchangeStackCard,
    takeHiddenCard,
    continue,
    end
}

export class Skyjo {
    id: number = 0;
    round: number = 0;
    players: Players = new Players();
    pioche: Pioche = new Pioche();
    gameRound: GAMEROUNDS = GAMEROUNDS.start;
    animation: Animate = new Animate;
    isLastTurn: boolean = false

    constructor(assign?: Skyjo, pseudo?: string) {
        if (assign) {
            Object.assign(this, assign);
            this.players = Object.assign(new Players(), this.players);
            this.players.forEach((player, index) => {
                this.players[index] = Object.assign(new Player(), player);
                player.cards.forEach((card, index) => {
                    player.cards[index] = card ? Object.assign(new Card(), card) : undefined;
                });
            });
            this.pioche = Object.assign(new Pioche(), this.pioche);
            this.pioche.stack.forEach((card, index) => {
                this.pioche.stack[index] = Object.assign(new Card(), card);
            });
            this.pioche.discard.forEach((card, index) => {
                this.pioche.discard[index] = Object.assign(new Card(), card);
            });

            this.pioche.choosenCard = this.pioche.choosenCard ? Object.assign(new Card(), this.pioche.choosenCard) : undefined;

            this.animation = Object.assign(new Animate(), this.animation);
            this.players.setMe(pseudo)
            return this
        }
    }

    async save() {
        this.round++;
        return axios.post('/api/skyjo/update/' + this.id, {
            game: this,
            turn: this.round
        }).then((res) => {
            if (res.data.refresh) {
                socket.emit("refresh", this.id);
                return false
            }
            else {
                socket.emit("skyjo", this);
                socket.emit("lobby", this.players.me?.pseudo);
                return true
            }
        })
    }

    get canJoinTheGame(): boolean { return (this.isGameRound('start') && this.players.canJoinThePlayers) }

    get canStartTheGame(): boolean { return this.isGameRound('start') && !!this.players.me?.creator && this.players.isEnoughPlayers }

    get waitEnoughPlayers(): boolean { return this.isGameRound('start') && !this.players.isEnoughPlayers }

    get waitCreatorToStart(): boolean { return this.isGameRound('start') && !this.players.me?.creator && this.players.isEnoughPlayers }

    get canContinue(): boolean { return this.isGameRound('continue') && !!this.players.me?.creator }

    get canRestart(): boolean { return this.isGameRound('end') && !!this.players.me?.creator }


    startGame() {
        this.players.forEach((player) => player.cards = this.pioche.stack.splice(-12, 12))
        this.gameRound = GAMEROUNDS.chooseTwoCards;
        this.save()
    }

    continueGame() {
        this.animation.reset()
        this.pioche = new Pioche()
        this.startGame()
    }

    startOverGame() {
        this.players.resetScores()
        this.players.resetCloser()
        this.startGame()
    }

    canGetDiscardCard(card: Card): boolean { return this.isGameRound('chooseStackOrDiscard') && this.players.isMyTurn && card === this.pioche.lastCardFromDiscard() }

    canGetStackCard(card: Card): boolean { return this.isGameRound('chooseStackOrDiscard') && this.players.isMyTurn && card === this.pioche.lastCardFromStack() }

    get canGetDiscardOrStackCard() { return this.isGameRound('chooseStackOrDiscard') && this.players.isMyTurn }

    get canKeepOrDiscardNewCard() { return this.isGameRound('keepOrDiscard') && this.players.isMyTurn }

    getDiscardCard(card: Card) {
        if (!this.canGetDiscardCard(card))
            return
        const stackCard = this.pioche.discard.pop()
        if (!stackCard)
            return
        this.pioche.choosenCard = stackCard
        this.gameRound = GAMEROUNDS.exchangeDiscardCard;
        this.animation.reset().add(stackCard, false, 'discard')
        this.save()

    }

    getStackCard(card: Card) {
        if (!this.canGetStackCard(card))
            return
        this.pioche.lastCardFromStack().show = true
        this.gameRound = GAMEROUNDS.keepOrDiscard;
        this.animation.reset().add(this.pioche.lastCardFromStack(), true)
        this.save()
    }

    keepNewCard() {
        if (!this.canKeepOrDiscardNewCard)
            return
        const stackCard = this.pioche.stack.pop()
        if (!stackCard)
            return
        this.pioche.choosenCard = stackCard
        this.gameRound = GAMEROUNDS.exchangeStackCard;
        this.animation.reset().add(stackCard, false, 'stack')
        this.save()
    }

    discardNewCard() {
        if (!this.canKeepOrDiscardNewCard)
            return
        const stackCard = this.pioche.stack.pop()
        if (!stackCard)
            return
        this.pioche.discard.push(stackCard)
        this.gameRound = GAMEROUNDS.takeHiddenCard;
        this.animation.reset().add(stackCard, false, 'stack')
        this.save()
    }

    isGameRound(step: string): boolean { return this.gameRound === GAMEROUNDS[step as keyof typeof GAMEROUNDS] }

    isPlayerRound(player: Player): boolean {
        if (this.isGameRound('start') || this.isGameRound('end') || this.isGameRound('continue'))
            return false
        if (this.isGameRound('chooseTwoCards') && !player.hasReturnTwoCards)
            return true
        return player === this.players.isPlaying
    }

    playerReturnTwoCards(card: Card) {
        card.show = true
        this.animation.reset().add(card, true)
        if (this.players.allHasReturnTwoCards) {
            this.gameRound = GAMEROUNDS.chooseStackOrDiscard;
            this.players.setBeginner()
        }
        this.save()
    }

    exchangePlayerCard(player: Player, card: Card, cardIndex: number) {
        if (!this.pioche.choosenCard)
            return

        const cardIsReturned = !card.show
        card.show = true

        player.cards[cardIndex] = this.pioche.choosenCard
        this.pioche.choosenCard = undefined
        this.pioche.discard.push(card)

        const idPlayerCardPlace = 'place' + player.id + '-' + cardIndex
        const playerNewCard = player.cards[cardIndex]
        if (!playerNewCard)
            return
        this.animation.add(card, cardIsReturned, idPlayerCardPlace)
            .add(playerNewCard, false, 'choosenCard')
    }

    isClickablePlayerCard(player: Player, card: Card): boolean {
        if (!this.isPlayerRound(player) || !player.isMyBoard)
            return false

        if (this.isGameRound('chooseTwoCards'))
            return !player.hasReturnTwoCards && !card.show

        if (this.isGameRound('exchangeDiscardCard') || this.isGameRound('exchangeStackCard'))
            return true

        if (this.isGameRound('takeHiddenCard') && !card.show)
            return true

        return false
    }

    clickPlayerCard(player: Player, card: Card, cardIndex: number): void {
        if (!this.isClickablePlayerCard(player, card))
            return

        if (this.isGameRound('chooseTwoCards')) {
            this.playerReturnTwoCards(card)
            return
        }

        this.animation.reset()
        if (this.isGameRound('takeHiddenCard')) {
            this.animation.add(card, true)
            card.show = true
        }

        if (this.isGameRound('exchangeDiscardCard') || this.isGameRound('exchangeStackCard')) {
            this.exchangePlayerCard(player, card, cardIndex)
        }

        this.lastTurn(player)
        player.checkColumn(this)
        this.players.nextPlayer()
        if (this.players.isPlaying && this.players.isPlaying === this.players.closer)
            this.endOfGame()
        else
            this.gameRound = GAMEROUNDS.chooseStackOrDiscard;
        this.save()
    }

    lastTurn(player: Player) {
        if (this.players.closer)
            return
        if (!player.isLastTurn)
            return
        this.isLastTurn = true
        this.players.setCloser(this.players.isPlaying!)
    }

    endOfGame(): void {
        this.players.returnAllCards(this)
        this.players.calculScores()
        this.players.resetPlaying()
        this.gameRound = this.players.maxHighScore >= 100 ? GAMEROUNDS.end : GAMEROUNDS.continue
        this.isLastTurn = false
    }
}
